import { CIActionBtnGroup, CIStatusCapsule } from 'ccs-ui-components';
import { Dispatch, SetStateAction, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AssociationList from '../../association-list/AssociationList';

import { EVENT_ASSOCIATION_TABS, EVENT_ROUTE, SERVICE_MODE, SORT_STRINGS, URL_PATH, associationEventType } from '../../../../../constants';
import { IService, IServiceResponseModel } from '../../../../../models';
import AssociationTogglerButtons from '../../association-toggler-buttons/AssociationTogglerButtons';
import classes from './ServiceAssociationList.module.scss';

interface IServiceList {
  setSelectedService: Dispatch<SetStateAction<IService>>,
  selectedService: IService,
  queryParams: { [key: string]: string };
  setQueryParams: Dispatch<SetStateAction<{ [key: string]: string; }>>,
  isLoading: boolean,
  errorMessage: string,
  serviceData: IServiceResponseModel
}

const CIGridLabels = {
  SERVICE_NAME: 'serviceName',
  SERVICE_STATUS: 'status',
};

const ServiceAssociationList = ({ setSelectedService, selectedService, serviceData, setQueryParams, queryParams, errorMessage, isLoading }: IServiceList) => {
  const sortedObj = { sortedName: queryParams.sort, sortedOrder: queryParams.type };
  const { t: translate } = useTranslation();
  const route = useLocation();
  const { eventName = '', eventVersion = '' } = useParams();
  const [selectedSortField, setSelectedSortField] = useState(queryParams.sort);
  const [, setSelectedSortFieldOrder] = useState<boolean>(true);
  const [eventType, setEventType] = useState(associationEventType.INGRESS);

  const isSelectedRow = (row: IService) => row.uuid === selectedService.uuid;

  /**
  * @method renderServiceNameAndVersion
  * @description render journey name and version
  */
  const renderServiceNameAndVersion = (row: IService) => (
    <div className={`${classes['service-name-wrapper']} ${
          isSelectedRow(row) ? classes['selected-row'] : ''
        }`}
    >
      <p className={classes['service-name']} title={row.serviceName}>{row.serviceName}</p>
      {row.version && <p className={classes['service-version']} title={`Ver ${row.version}`}>{`Ver ${row.version}`}</p>}
    </div>
  );

  /**
   * Renders the status capsule component based on the provided row status.
   * @param row - The row object containing the status property.
   * @returns The CIStatusCapsule component if the row status is truthy, otherwise null.
   */
  const renderStatus = (row: IService) => (
    row.status && <div className={isSelectedRow(row) ? classes['selected-row'] : ''}> <CIStatusCapsule status={row.status} /> </div>
  );

  const navigate = useNavigate();
  /**
   * @method navigateToServicePreview
  * @description To navigate to view service screen
  * @param name
  */
  const navigateToServicePreview = (
    serviceName: string,
    serviceVersion: string
  ) => {
    navigate(
      `${URL_PATH.SERVICE_HOME}${URL_PATH.VIEW_SERVICE}/${serviceName}/${serviceVersion}/${SERVICE_MODE.VIEW}`,
      {
        state: {
          ...route.state,
          path: route.search,
          from: `${EVENT_ROUTE.ASSOCIATIONS_ROUTE}${eventName}/${eventVersion}/${EVENT_ASSOCIATION_TABS.SERVICES}/${eventType}`
        }
      }
    );
  };

  /**
   * Renders the action cell with view control and row selection arrow indicator.
   * @param row - The row object.
   * @returns The rendered action cell.
   */
  const renderActionCell = (row: IService) => (
    <div className={classes['service-action-cell']}>
      <CIActionBtnGroup buttons={[{
        label: translate('info_message.lbl_service_navigation'),
        key: 'viewIcon',
        onClick: () => {
          navigateToServicePreview(row.serviceName, `${row.version}`);
        },
        testId: 'service-nav-button',
      }]}
      />
      <i
        className={`bi bi-chevron-right ${classes['row-arrow-icon']}`}
        aria-hidden="true"
        data-testid="service-row-arrow"
      />
    </div>
  );

  /**
  * @method sortHandler
  * @description Handles the sorting of the event list based on the selected label and state.
  * @param label - The label to sort by.
  * @param selectedLabelState - The state of the selected label (true for ascending, false for descending).
  */
  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre) => ({
      ...pre,
      sort: label,
      type: selectedLabelState ? SORT_STRINGS.ASC : SORT_STRINGS.DESC
    }));
  };

  /**
  * @method handleSortClick
  * @description Handles the click service for sorting the service list.
  */
  const handleSortClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder((preValue) => {
        sortHandler(selectedSortField, !preValue);
        return !preValue;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };

  const headers = [
    {
      label: translate('service_list_component.lbl_service_name'),
      name: CIGridLabels.SERVICE_NAME,
      sortOptions: {
        callback: handleSortClick,
      },
      options: { breakPointWidth: 6 },
      columnData: { field: '', render: renderServiceNameAndVersion },
    },
    {
      label: translate('service_list_component.lbl_status'),
      name: CIGridLabels.SERVICE_STATUS,
      options: { breakPointWidth: 5 },
      sortOptions: {
        callback: handleSortClick
      },
      columnData: { field: '', render: renderStatus },
    },
    {
      name: 'action',
      options: { breakPointWidth: 1 },
      columnData: { field: '', render: renderActionCell, className: 'render-action' },
    }
  ];

  /**
   * @method onGetPageHandler
   * @description Handles the service when a page number is selected.
   * @param {number} pageNumber - The selected page number.
   */
  const onGetPageHandler = (pageNumber: number) => {
    setQueryParams((pre) => ({
      ...pre,
      page: String(pageNumber)
    }));
  };

  /**
   * @method onGetItemsHandler
   * @description Handles the service when an item is selected.
   * @param itemNumber - The number of the selected item.
   */
  const onGetItemsHandler = (itemNumber: number) => {
    setQueryParams((pre) => ({
      ...pre,
      item: String(itemNumber)
    }));
  };

  const footerOptionsObj = {
    itemsPerPage: Number(queryParams.item),
    currentPageNumber: Number(queryParams.page),
    numberOfPages: serviceData?.numberOfPages,
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  const rowClickHandler = (row: IService) => {
    setSelectedService(row);
  };

  const buttonClickHandler = (value: string) => {
    setEventType(value);
    navigate(
      `${EVENT_ROUTE.ASSOCIATIONS_ROUTE}${eventName}/${eventVersion}/${EVENT_ASSOCIATION_TABS.SERVICES}/${value}`
    );
  };

  return (
    <div className={classes['service-list-container']}>
      <AssociationTogglerButtons buttonClickHandler={buttonClickHandler} />
      <AssociationList headers={headers} tableData={serviceData?.services} sorted={sortedObj} footer={footerObj} dataRowClick={rowClickHandler} errorMessage={errorMessage} isLoading={isLoading} gridClass={classes['service-association-grid']} />
    </div>
  );
};

export default ServiceAssociationList;
